import { useGraphStore } from "@modules/data/stores/graph.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import { Source, WorkflowEnedis } from "@types"
import { Services } from "@services"
import { computed, inject, ref } from "vue"
import * as Sentry from "@sentry/vue"
import { useI18n } from "vue-i18n"
import { formatIdSource, formatDate } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"
import { useFetchWrapper } from "@core/composables"
import {
    avancementEnedis,
    fluxEnRetard,
    signatureDesFlux,
    fluxCommandesNonPassees,
    signatureDesCommandes,
    LIBELLE_STREAM_EN_RETARD,
    LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU,
    LIBELLE_COMMANDE_NON_PASSEE,
    LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE,
    type AvancementEnedis,
} from "./workflow-enedis-avancement.helper"

/** Message de bannière prêt à afficher : texte + canal visuel. */
export type WorkflowEnedisBannerMessage = {
    text: string
    severity: "info" | "warn"
}

/**
 * Format d'affichage d'une ÉCHÉANCE de mise à disposition : elle porte une heure,
 * parce qu'à l'échelle de « quelques heures » une date nue ne dit rien.
 */
const FORMAT_ECHEANCE = "DD/MM/YYYY [à] HH[h]mm"

/**
 * Workflows dont le libellé est défini explicitement dans les messages i18n
 * ci-dessous, avec le canal visuel de chacun. Les clés sont le CONTRAT partagé
 * avec api-metier (`workflow.enedis.verdict.js`) — ne pas renommer sans lui.
 *
 * ⚠️ La correspondance est par nom EXACT (cf. plus bas) : tout libellé émis par
 * api-metier et absent d'ici retombe sur `COPIE_REPLI_LIBELLE_INCONNU`, une copie qui
 * n'affirme RIEN (DEV-1624, lot 1 du dossier C2). Elle a remplacé
 * `data_retrieval_in_progress`, qui affirmait qu'une récupération était EN COURS et
 * qu'elle SE TERMINERAIT : pour un libellé qui signifie l'inverse, cette retombée
 * n'était pas une dégradation propre, c'était une affirmation fausse. Un nouveau libellé
 * côté api-metier reste à livrer ici dans la même livraison, mais l'ordre de déploiement
 * est redevenu une PRÉFÉRENCE et non une contrainte dure.
 *
 * Canal visuel :
 * - `info` : récupération réellement en cours ou attente normale — la bannière
 *   disparaît quand la donnée arrive ;
 * - `warn` : blocage contractuel qui NOMME sa cause (DEV-1502, famille
 *   DEV-1460/1502/1503) — rien n'est « en cours », et une action du client peut
 *   rouvrir la collecte. Exception voulue :
 *   `historique_souscription_cdc_absente` reste en `info`, car l'opposition à la
 *   courbe de charge est un choix sincère du client (SGT4H7), pas une panne.
 * - `historique_identifiant_inconnu` est déclaré en avance de phase (owner
 *   `homeys`, servi par un lot api-metier parallèle) : tant que le backend ne
 *   l'émet pas, la clé est simplement inerte.
 * - `historique_souscription_absente` (DEV-1584, owner `homeys`) : le canal de
 *   mise à jour quotidienne n'est pas armé de NOTRE côté (aucune souscription
 *   R6X active pour un compteur au régime quotidien). Canal `info` : la cause
 *   est nommée, ET ce que nous en faisons. La copie ne donne toujours AUCUN
 *   délai et ne dit pas au client qu'il n'a rien à faire (aucun champ servi ne
 *   soutient cette phrase, et la cause du client, consentement ou contrat, peut
 *   être illisible en amont). Ne jamais y écrire « auprès d'Enedis » : le retard
 *   n'est pas le sien.
 *
 *   ⚠️ La reprise, elle, est DÉSORMAIS affirmable, et c'est un fait mesuré, pas
 *   une formule de politesse (DEV-1584, remédiation du 04/09) : avant, un
 *   compteur servi par ce libellé pouvait n'être repris par AUCUN job. Mesuré en
 *   prod le 2026-09-04 08:14Z : 29 des 31 compteurs concernés étaient des échecs
 *   à code transitoire périmés par la borne d'âge du ramp, que le balayage
 *   mensuel ne prenait pas non plus. `balayage.population_perimee` (enedis#320)
 *   referme ce trou, et l'invariant REC-I23 du battement compte chaque matin les
 *   compteurs qu'aucun job ne reprend : la phrase « nous la relançons » est
 *   gardée par un rouge, pas par une intention. Si cet invariant devait rougir
 *   durablement, c'est CETTE phrase qu'il faudrait retirer. Déclarée AVANT le lot api-metier qui l'émet (ordre de déploiement :
 *   front d'abord), inerte tant qu'il n'est pas servi. Sa phrase « données déjà
 *   reçues » n'est servie que si `verdict.last_data_date` l'est aussi (cf.
 *   `aDejaRecuDesDonnees` plus bas) — sinon `historique_souscription_absente_sans_donnee`.
 * - `historique_consentement_absent` (DEV-1612, revue des résidus de la
 *   résiliation A1 du 04/09) : même garde, même raison. « L'historique déjà
 *   collecté reste disponible » n'est vraie QUE si `verdict.last_data_date` est
 *   servi ; mesure A1 du 04/09 : 326 des 429 verdicts qui basculent n'ont
 *   jamais reçu de donnée. La phrase est donc OMISE (pas remplacée) quand le
 *   champ manque, via `historique_consentement_absent_sans_donnee` — même
 *   mécanique de scission que `historique_souscription_absente`, cf.
 *   `SCISSION_PAR_DONNEE` plus bas.
 * - `historique_souscription_absente_hors_capacite` (DEV-1584, variante « hors
 *   capacité » validée le 04/09, owner `homeys`) : MÊME cause que le libellé
 *   ci-dessus, mais la reprise n'existe pas. api-metier ne sert cette clé que
 *   quand le verdict porte `evidence.hors_capacite` : le dossier Enedis du
 *   point refuse la capacité de souscrire en soutirage, la sélection des jobs
 *   de souscription écarte ce compteur avant tout appel, et ni le ramp
 *   quotidien ni le balayage mensuel ne le commanderont. Écrire « nous la
 *   relançons » y serait une promesse que la machine refuse de tenir, et c'est
 *   précisément l'impasse déguisée que la décision du 04/09 exclut.
 *
 *   Canal `info` et NON `warn` : ce n'est pas un blocage contractuel au sens de
 *   DEV-1502. Aucune action du client ne rouvre cette porte, donc rien ne
 *   justifie de l'alerter ; on lui dit d'où vient le refus, et on s'arrête là.
 *   Ne jamais y ajouter « contactez le support » ni un geste à faire : aucun
 *   champ servi ne dit lequel serait utile. Même scission par la donnée que le
 *   libellé nu (`SCISSION_PAR_DONNEE`). Déclarée AVANT le lot api-metier qui
 *   l'émet et AVANT l'enedis qui sert le fait : inerte jusque-là.
 * - `historique_commande_non_passee` (DEV-1661, owner `homeys`) : le retard est
 *   le NÔTRE. Tous les flux dus en retard du compteur sont des commandes M023
 *   dont la dernière est close et que personne n'a repassées (budget d'affaires
 *   saturé, ou planificateur). Avant enedis#350 ce compteur lisait
 *   `historique_stream_en_retard` avec « Nous la redemandons à Enedis » sur une
 *   commande jamais partie (8 C5 du parc EM le 07/09, contrôle PH-29). La copie
 *   nomme le côté, le flux, sa dernière réception et notre dernière demande
 *   (`streams_en_retard[].derniere_commande.commanded_at`, relayé par api-metier),
 *   dit que la demande part avec notre prochain lot SANS date (aucun champ servi
 *   ne la porte) et rassure sur les autres mesures, vraie par construction du
 *   verdict (tout autre flux dû est à l'heure) et retirée sans donnée reçue
 *   (`SCISSION_PAR_DONNEE`). Canal `info` : cause à nous à action automatique
 *   connue (la boucle de commande), pas une panne. Jamais « redemandons à
 *   Enedis », jamais « écrit à Enedis », jamais un mot de budget ou de
 *   planificateur. Déclarée AVANT le lot api-metier qui l'émet : inerte jusque-là.
 */
const LIBELLE_SOUSCRIPTION_ABSENTE = "historique_souscription_absente"
const LIBELLE_SOUSCRIPTION_ABSENTE_SANS_DONNEE =
    "historique_souscription_absente_sans_donnee"
const LIBELLE_SOUSCRIPTION_ABSENTE_HORS_CAPACITE =
    "historique_souscription_absente_hors_capacite"
const LIBELLE_SOUSCRIPTION_ABSENTE_HORS_CAPACITE_SANS_DONNEE =
    "historique_souscription_absente_hors_capacite_sans_donnee"
const LIBELLE_CONSENTEMENT_ABSENT = "historique_consentement_absent"
const LIBELLE_CONSENTEMENT_ABSENT_SANS_DONNEE =
    "historique_consentement_absent_sans_donnee"
const LIBELLE_RETARD_CONFIRME = "historique_retard_confirme"
const LIBELLE_RETARD_CONFIRME_SANS_DONNEE =
    "historique_retard_confirme_sans_donnee"
const LIBELLE_PAS_DE_MAJ_QUOTIDIENNE =
    "historique_pas_de_maj_quotidienne_souscrite"
const LIBELLE_PAS_DE_MAJ_QUOTIDIENNE_SANS_DONNEE =
    "historique_pas_de_maj_quotidienne_souscrite_sans_donnee"

/**
 * COMPTEUR NON TÉLÉOPÉRABLE (27/10/2026, décision et textes validés par Mathieu).
 *
 * Enedis refuse la souscription quotidienne R6X en SGT589 (« compteur actuellement pas
 * téléopérable ») : il ne peut plus relever le compteur à distance. Le verdict enedis-2026
 * émet `owner: "enedis"`, `sub_cause: "compteur_non_teleoperable"` depuis enedis v209
 * (homeys-api-enedis#507) ; api-metier (`workflow.enedis.verdict.js`) en tire la clé
 * stable ci-dessous. Déclarée AVANT le lot api-metier qui l'émet (ordre : front d'abord),
 * inerte tant qu'il ne la sert pas.
 *
 * Canal `info` : aucune action ne revient au client (la télé-relève se rétablit chez
 * Enedis), et un `warn` l'inviterait à un geste qui n'existe pas. Ce que la copie
 * affirme, elle le lit : la date de dernière mesure (`{derniere_mesure}`, d'où
 * `CLES_QUI_CITENT_LA_DERNIERE_MESURE`) et « l'historique déjà collecté reste
 * disponible », vraie SEULEMENT si `verdict.last_data_date` est servi. Sans lui, la
 * variante `_sans_donnee` (choisie PAR LE FRONT, `SCISSION_PAR_DONNEE`) ne date rien et
 * dit « démarrera » au lieu de « reprendra ».
 */
const LIBELLE_COMPTEUR_NON_TELEOPERABLE = "historique_compteur_non_teleoperable"
const LIBELLE_COMPTEUR_NON_TELEOPERABLE_SANS_DONNEE =
    "historique_compteur_non_teleoperable_sans_donnee"

/**
 * ABSENCE INSTRUITE — le « trou connu chez Enedis » (DEV-1650, décision écrite de
 * Mathieu du 07/09, dossier C2 point 14).
 *
 * Sa décision, mot pour mot : « Une fois qu'on sait qu'il y a un trou, il faut le
 * noter, et ne plus redemander et dire au client qu'il y a un trou chez enedis connu
 * pour pas qu'il écrive au support. » Cette copie EST le « dire », et son intention est
 * d'ÉVITER un ticket support : elle nomme le fait, sa période, sa date de constat, et
 * elle dit que nous ne redemandons pas.
 *
 * Canal `info` : état stable, aucune action ne revient au client (règle des niveaux
 * ci-dessous). Un `warn` inviterait à agir là où il n'y a rien à faire.
 *
 * ⚠️ Ce que la copie ne dit JAMAIS :
 * - « Enedis a répondu par écrit » / « par écrit » : sur un trou constaté, personne
 *   chez Enedis n'a rien écrit — nous l'avons CONSTATÉ (refus de Mathieu du 04/09,
 *   redit le 07/09) ;
 * - « n'existe pas chez Enedis » / « n'arrivera pas » : la plage est RÉFUTABLE, une
 *   donnée qui arrive plus tard la lève (DEV-1615). D'où « si elles apparaissent, nous
 *   les intégrerons » — une promesse que la machine tient réellement.
 *
 * Deux variables sont des FRAGMENTS composés par le front, pas des dates nues :
 * `{periode}` (« du … au … », plus « , par périodes » quand les plages sont disjointes,
 * et un repli sans date quand la plage est ouverte sur le passé) et `{constat}` (la
 * PROVENANCE : « à la réception de notre commande d'historique » ou « par consultation
 * directe »). Une clé de moins par combinaison, et la même phrase validée.
 */
const LIBELLE_ABSENCE_INSTRUITE = "historique_absence_instruite"
const LIBELLE_ABSENCE_INSTRUITE_SANS_DONNEE =
    "historique_absence_instruite_sans_donnee"

/**
 * Incident INTERNE : la collecte est interrompue de NOTRE côté (DEV-1625, dossier C2
 * §2.3). Règle de Mathieu, mot pour mot (04/09) : « C'est un message où on doit
 * absolument être informés avant le client et on doit toujours donner un élément
 * rassurant. »
 *
 * Les deux moitiés de cette phrase sont ici :
 * - **informés avant le client** : `verdict.incident.notifie_le` porte la date à
 *   laquelle un canal push prouvé nous a prévenus. La copie la CITE (`{informe_le}`),
 *   et `incidentServable()` refuse de rendre quoi que ce soit sans elle. Ce n'est pas
 *   une double garde inutile : api-metier n'émet déjà la clé qu'avec la preuve, mais
 *   une copie qui affirme « notre équipe en a été informée le … » ne doit pas pouvoir
 *   être servie par un front qui n'a pas lu la date qu'il affiche ;
 * - **un élément rassurant** : `{rattrapage}`, servi UNIQUEMENT si
 *   `incident.rattrapage_automatique` est vrai — quatre conditions mesurées côté
 *   enedis (régime EM, souscription R6X active, sub_cause avec attendu, incident de
 *   moins de 30 jours). Jamais déduit ici, jamais servi par défaut.
 *
 * ⚠️ Aucune référence d'incident n'est affichée (§4 point 9, décision du 04/09) :
 * aucun outil support ne la résout, et un client qui cite une référence introuvable est
 * moins bien servi que celui à qui on n'en donne aucune.
 *
 * Clé DISTINCTE pour la portée parc (et non une variante) : la phrase ne nomme aucun
 * compteur, et le regroupement doit servir UNE bannière plutôt qu'une par point.
 */
const LIBELLE_INCIDENT_HOMEYS = "historique_incident_homeys"
const LIBELLE_INCIDENT_HOMEYS_SANS_DONNEE =
    "historique_incident_homeys_sans_donnee"
const LIBELLE_INCIDENT_HOMEYS_PARC = "historique_incident_homeys_parc"
const LIBELLE_INCIDENT_HOMEYS_PARC_SANS_DONNEE =
    "historique_incident_homeys_parc_sans_donnee"

/**
 * L'ÉLÉMENT RASSURANT, texte exact du dossier C2 §2.3.
 *
 * Une seule phrase, et une seule promesse : la re-commande automatique des jours
 * manqués est le SEUL rattrapage que nous MESURONS (`FLUX_FALLBACK_DAYS = 30` côté
 * enedis, fenêtre glissante depuis aujourd'hui). Elle commence par une espace parce
 * qu'elle s'insère entre deux phrases (`…application.{rattrapage} Vous n'avez…`) ;
 * vide, la ponctuation reste correcte.
 *
 * ⚠️ Aucune date, aucun délai, aucun « pendant 30 jours » : la rotation était saturée
 * le 03/09 (`sent: 0`, `quota_skipped: 17`) et la fenêtre glisse. La phrase dit CE QUE
 * la machine fait, jamais QUAND elle l'aura fait.
 */
const RATTRAPAGE_AUTOMATIQUE =
    " Une fois la collecte rétablie, les jours manquants sont redemandés automatiquement à Enedis."

/** Les quatre clés d'incident — celles que `incidentServable()` garde. */
const LIBELLES_INCIDENT = new Set<string>([
    LIBELLE_INCIDENT_HOMEYS,
    LIBELLE_INCIDENT_HOMEYS_SANS_DONNEE,
    LIBELLE_INCIDENT_HOMEYS_PARC,
    LIBELLE_INCIDENT_HOMEYS_PARC_SANS_DONNEE,
])

/**
 * Une copie d'incident peut-elle être servie pour CE workflow ?
 *
 * FERMÉE PAR DÉFAUT : sans `verdict.incident.notifie_le`, `false`. Un workflow qui
 * porte une clé d'incident sans la date est ÉCARTÉ (aucun message), jamais rendu avec
 * une date manquante ni renvoyé sur le repli « cette version ne sait pas l'afficher »
 * (qui serait faux : elle sait, c'est la preuve qui manque).
 */
const incidentServable = (workflow: WorkflowEnedis): boolean =>
    !LIBELLES_INCIDENT.has(workflow.name) ||
    Boolean(workflow.verdict?.incident?.notifie_le)

/**
 * Copie servie quand api-metier émet un `name` que CETTE version du front ne connaît pas
 * (texte validé de la PR #497, reprise ici par DEV-1624).
 *
 * ⚠️ CE REPLI ÉTAIT UNE AFFIRMATION FAUSSE, et c'était une contrainte de déploiement
 * déguisée. Un nom inconnu retombait sur `data_retrieval_in_progress` : « les données de
 * votre compteur sont **en cours de récupération auprès d'Enedis**, le chargement **sera
 * terminé** lorsque ce message ne sera plus affiché ». Trois affirmations positives, en
 * `info`, servies pendant la fenêtre où api-metier est déployé et pas le front, à des
 * libellés qui disent l'exact contraire.
 *
 * Cette copie-ci ne peut pas être fausse : la seule chose qu'un `name` inconnu garantit,
 * c'est qu'api-metier a jugé qu'il y avait quelque chose à dire sur ce compteur. Elle ne
 * nomme aucun responsable, ne promet aucun délai et n'affirme aucune récupération en
 * cours. Le décalage, lui, est remonté en télémétrie (`signalerLibellesInconnus`) : ce
 * n'est pas un état normal, c'est un écart de déploiement qui doit se voir.
 */
const COPIE_REPLI_LIBELLE_INCONNU = "data_retrieval_unknown_label"

/**
 * Libellés dont la copie CITE la date de la dernière mesure (`{derniere_mesure}`).
 *
 * Deux conséquences, et elles sont indissociables :
 * 1. le groupe de bannière porte cette date (cf. la clé de regroupement plus bas) —
 *    sans quoi deux compteurs aux dates différentes fondraient en un seul message
 *    portant UNE date, fausse pour l'un des deux ;
 * 2. la date ne doit pas être servie une SECONDE fois par la phrase d'avancement, d'où
 *    `CLES_SANS_AVANCEMENT` côté helper (DEV-1624).
 */
const CLES_QUI_CITENT_LA_DERNIERE_MESURE = new Set<string>([
    LIBELLE_RETARD_CONFIRME,
    LIBELLE_PAS_DE_MAJ_QUOTIDIENNE,
    // DEV-1625 : la copie d'incident cite `{derniere_mesure}` — donc elle entre
    // dans la clé de regroupement, sinon deux compteurs à dates différentes
    // partageraient une bannière qui n'en cite qu'une.
    LIBELLE_INCIDENT_HOMEYS,
    // 27/10 : la copie du compteur non téléopérable cite `{derniere_mesure}`.
    LIBELLE_COMPTEUR_NON_TELEOPERABLE,
])

/**
 * Libellés dont la copie NOMME et DATE des flux (`verdict.streams_en_retard`), et dont
 * la phrase d'action dépend de `verdict.recommande_en_vol` (DEV-1632).
 *
 * Même conséquence que ci-dessus, sur trois faits au lieu d'un : le groupe de bannière
 * porte la signature des flux ET l'état de la re-commande. Deux compteurs dont des flux
 * différents manquent, ou dont l'un est redemandé et l'autre non, ne peuvent pas
 * partager une phrase qui cite l'un des deux.
 */
const CLES_QUI_CITENT_LES_FLUX = new Set<string>([LIBELLE_STREAM_EN_RETARD])

/**
 * Libellés dont la copie CITE les faits d'une absence instruite (DEV-1650) : la
 * période, la date du constat et la provenance.
 *
 * Même conséquence que pour les flux et les dates : ces faits entrent dans la clé de
 * regroupement. Deux compteurs dont les trous ne tombent pas aux mêmes dates, ou n'ont
 * pas été constatés de la même façon, ne peuvent pas partager une bannière qui n'en
 * cite qu'un — elle serait fausse pour l'autre.
 */
const CLES_QUI_CITENT_LABSENCE = new Set<string>([
    LIBELLE_ABSENCE_INSTRUITE,
    LIBELLE_ABSENCE_INSTRUITE_SANS_DONNEE,
])

/**
 * Libellés dont la copie NOMME les flux dus en retard PAR NOUS, DATE leur dernière
 * réception et notre dernière demande (DEV-1661).
 *
 * Même conséquence : ces faits entrent dans la clé de regroupement (`signatureDesCommandes`).
 * Deux compteurs dont les demandes ne datent pas du même jour ne partagent pas une
 * bannière qui n'en cite qu'une.
 */
const CLES_QUI_CITENT_LES_COMMANDES = new Set<string>([
    LIBELLE_COMMANDE_NON_PASSEE,
    LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE,
])

/**
 * Libellés inconnus DÉJÀ remontés dans cette session de navigation.
 *
 * `getWorkflowEnedisMessageList` est un `computed` : il se réévalue à chaque changement
 * de store. Un envoi par évaluation ferait du bruit sans ajouter d'information — un nom
 * inconnu est un fait de version, pas un événement qui se répète.
 */
const libellesInconnusSignales = new Set<string>()

/**
 * Remonte les libellés sans copie dans cette version du front, une fois par nom.
 * @param workflows - Workflows retombés sur `COPIE_REPLI_LIBELLE_INCONNU`
 */
const signalerLibellesInconnus = (workflows: WorkflowEnedis[]) => {
    const nouveaux = [...new Set(workflows.map((w) => w.name))].filter(
        (name) => !libellesInconnusSignales.has(name)
    )
    if (nouveaux.length === 0) return
    nouveaux.forEach((name) => libellesInconnusSignales.add(name))
    Sentry.captureMessage(
        `[workflow_enedis] libellé sans copie dans cette version du front : ${nouveaux.join(", ")}`,
        { level: "warning", extra: { libelles: nouveaux } }
    )
}

/**
 * Canal visuel par libellé — la RÈGLE, décidée par Mathieu le 04/09 (dossier C2 §4
 * point 4, DEV-1578) et appliquée ici :
 *
 * - **warn** : une action revient au client, OU une donnée manque durablement sans
 *   reprise automatique mesurée, OU la cause est une PANNE chez nous ;
 * - **info** : état stable, ou attente normale.
 *
 * ⚠️ « cause chez nous » se lit « panne chez nous ». Une cause à nous dont la reprise
 * est automatique et MESURÉE (`historique_souscription_absente` : ramp quotidien,
 * balayage mensuel, invariant REC-I23 du battement) n'est pas une panne, et reste en
 * `info` — décision du 04/09 sur le point 11 du même dossier. Le jour où l'invariant
 * rougit durablement, c'est la copie qu'il faut retirer, pas le canal qu'il faut monter.
 */
const WORKFLOW_ENEDIS_DEDICATED_COPY: Record<string, "info" | "warn"> = {
    historique_onboarding: "info",
    update_once: "info",
    historique_attente_enedis: "info",
    // DEV-1624 : `info` → `warn`. Le flux a décroché et aucune reprise automatique
    // n'est mesurée pour ce compteur : c'est la deuxième jambe de la règle
    // (« une donnée manque durablement sans reprise automatique mesurée »). La copie,
    // elle, ne dramatise pas et ne demande rien au client (décision du 04/09,
    // point 1 : « ou c'est simplement un trou »).
    historique_retard_confirme: "warn",
    // DEV-1624 : ce compteur n'a pas de mise à jour quotidienne souscrite. Rien ne
    // manque « durablement sans reprise » : il n'y a pas de dû quotidien du tout.
    // État stable ⇒ `info`, et aucune action demandée au client.
    historique_pas_de_maj_quotidienne_souscrite: "info",
    historique_pdl_hors_service: "warn",
    historique_consentement_absent: "warn",
    historique_souscription_cdc_absente: "info",
    historique_blocage_contractuel: "warn",
    historique_identifiant_inconnu: "warn",
    historique_souscription_absente: "info",
    historique_consentement_absent_sans_donnee: "warn",
    // Hors capacité : `info` comme le libellé nu. Un `warn` demanderait au client
    // un geste que rien ne lui permet de faire (cf. en-tête).
    historique_souscription_absente_hors_capacite: "info",
    // DEV-1625 — la cause est CHEZ NOUS et c'est une PANNE, pas une action automatique
    // connue : `warn`, sur la troisième jambe de la règle de niveau (« ou la cause est
    // chez nous »). Un incident en `info` ferait passer notre propre panne pour une
    // information neutre (dossier C2 §2.6).
    historique_incident_homeys: "warn",
    historique_incident_homeys_sans_donnee: "warn",
    historique_incident_homeys_parc: "warn",
    historique_incident_homeys_parc_sans_donnee: "warn",
    // 27/10 (SGT589) : `info`. La cause est chez Enedis et aucun geste du client ne
    // rétablit la télé-relève : un `warn` demanderait une action qui n'existe pas.
    [LIBELLE_COMPTEUR_NON_TELEOPERABLE]: "info",
    // DEV-1581 (A11) / DEV-1632 : `warn`. Deuxième jambe de la règle — une donnée
    // manque durablement (le flux a dépassé SON attendu, jugé côté enedis) et la
    // reprise n'est mesurée que lorsque le verdict porte une commande en vol ; quand
    // il n'en porte pas, la copie ne promet rien et le canal doit le refléter. Le
    // client, lui, n'a aucune action : la copie le dit, le canal ne le contredit pas
    // (c'est le même choix que `historique_retard_confirme`, DEV-1624).
    [LIBELLE_STREAM_EN_RETARD]: "warn",
    [LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU]: "warn",
    // ⚠️ DEV-1650 et DEV-1661 ONT ÉTÉ RETIRÉS de cette table le 08/09 (décision de
    // Mathieu citée dans `LIBELLES_SANS_BANNIERE`). Leurs libellés ne portent plus
    // aucune bannière : ils sont SILENCIEUX, pas inconnus. Voir la constante ci-dessous.
}

/**
 * Libellés que le front CONNAÎT et qu'il n'affiche VOLONTAIREMENT pas (décision de
 * Mathieu du 08/09 au matin).
 *
 * Sa décision, mot pour mot : « Il faut pas que l'outil devienne une bannière à
 * messages. L'affichage des trous, c'est plutôt un indicateur sur la page des données
 * brutes de la source / une manière de représenter la donnée à la date du trou avec une
 * légende qu'un ensemble de messages sinon on va pas s'en sortir je pense... »
 *
 * Les deux copies livrées la veille (le trou connu chez Enedis, DEV-1650 ; le retard de
 * commande qui est le nôtre, DEV-1661) restent écrites plus bas dans le dictionnaire,
 * mais plus rien ne les atteint : c'est l'INDICATEUR de la page « données brutes » qui
 * portera le trou, à sa date, avec sa légende.
 *
 * ⚠️ Pourquoi une liste plutôt qu'une simple suppression de la table du dessus : un
 * libellé absent des DEUX listes retombe sur `COPIE_REPLI_LIBELLE_INCONNU`, qui affiche
 * « cette version de l'application ne sait pas encore l'afficher. Contactez le support »
 * ET remonte une alerte Sentry. Ce serait une bannière de plus (le contraire de la
 * décision), un appel au support (le contraire de l'intention de DEV-1650) et un faux
 * signal de version. Ici, le silence est CHOISI et il est dit.
 *
 * ⚠️ Ce qui NE bouge pas : les types (`absence_instruite`, `streams_en_retard[].cause`,
 * `derniere_commande`) et leur relais depuis api-metier. L'indicateur à venir les lira.
 * Le jour où il existe, c'est ici qu'on décidera ce qui redevient visible.
 */
const LIBELLES_SANS_BANNIERE = new Set<string>([
    // DEV-1650 — le trou connu chez Enedis.
    LIBELLE_ABSENCE_INSTRUITE,
    LIBELLE_ABSENCE_INSTRUITE_SANS_DONNEE,
    // DEV-1661 — le retard de commande qui est le nôtre.
    LIBELLE_COMMANDE_NON_PASSEE,
    LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE,
])

export const useWorkflowEnedisMessage = (
    type: "building" | "source" = "building"
) => {
    const graphStore = useGraphStore()
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()

    // useScope: 'global' : un composant ne peut avoir qu'un seul composer i18n « local ».
    // PageBuildingData (entre autres) appelle déjà useI18n local avant ce composable ;
    // sans global, le 2e appel est ignoré et les clés workflow restent introuvables ([clé]).
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                update_once:
                    "Une mise à jour des données a été demandée pour le compteur {idsource}. Dès qu'Enedis nous enverra les nouvelles données, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures. | Une mise à jour des données a été demandée pour les compteurs {idsource}. Dès qu'Enedis nous les enverra, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures.",
                historique_onboarding:
                    "La récupération des données de votre compteur {idsource} a été demandée à Enedis. Dès qu'Enedis nous enverra les données, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures. | Les données des compteurs {idsource} ont été demandées à Enedis. Dès qu'Enedis nous les enverra, l'application sera mise à jour automatiquement. Cela prend de quelques minutes à quelques heures.",
                data_retrieval_in_progress:
                    "Les données de votre compteur {idsource} sont en cours de récupération auprès d'Enedis. Le chargement sera terminé lorsque ce message ne sera plus affiché. | Les données de vos compteurs {idsource} sont en cours de récupération auprès d'Enedis. Le chargement sera terminé lorsque ce message ne sera plus affiché.",
                // `historique_attente_enedis` : le compteur est en retard, mais DANS la fenêtre
                // où le SLA du flux rend la livraison encore attendue (api-metier
                // workflow.enedis.verdict.js). La copie de base ne date rien ; c'est la phrase
                // d'AVANCEMENT ajoutée en dessous qui porte l'échéance, quand le verdict la
                // soutient — et SEULEMENT alors : `expected_lag_days` absent ⇒ pas de phrase,
                // jamais un seuil de repli (cf. `workflow-enedis-avancement.helper.ts`).
                // ⚠️ La version précédente de ce commentaire affirmait
                // « `ENEDIS_STATUS_MODE_AUTO=1` sur `homeys-metier-prod`, lu le 31/08/2026,
                // donc l'attendu est résolu PAR PRM ». Cette affirmation est RETIRÉE : elle
                // n'a pas été re-vérifiée, et le sondage du parc la contredit plutôt qu'il ne
                // la soutient (203 PRM le 31/08/2026 : `mode: null` sur 203/203, et la SEULE
                // valeur non nulle d'`expected_lag_days` observée est `3`). Cf. le détail de
                // la mesure dans `workflow-enedis-avancement.helper.ts`.
                historique_attente_enedis:
                    "Les dernières mesures du compteur {idsource} ne nous sont pas encore parvenues. Ce délai reste dans ce que nous attendons pour ce compteur : nous les intégrerons dès qu'Enedis nous les transmettra. | Les dernières mesures des compteurs {idsource} ne nous sont pas encore parvenues. Ce délai reste dans ce que nous attendons : nous les intégrerons dès qu'Enedis nous les transmettra.",
                // `historique_retard_confirme` : le retard dépasse largement le SLA du flux — le
                // flux a décroché. ⚠️ Ne JAMAIS écrire ici « en cours de récupération », ni « le
                // chargement sera terminé », ni une durée : le backend a précisément conclu
                // l'inverse. Et ne pas affirmer qu'un signalement est ouvert chez Enedis — on ne
                // le fait pas systématiquement et on n'a pas qualité pour le faire (DEV-1491).
                //
                // ⚠️ RÉÉCRIT LE 04/09/2026 (DEV-1624, lot 1 du dossier C2, texte validé par
                // Mathieu). Trois défauts de la version d'avant, tous mesurés :
                // 1. elle ne DATAIT rien (« depuis un délai qui dépasse nettement celui que
                //    nous attendons ») alors que `last_data_date` est servi : le client
                //    lisait un jugement sans le fait qui le porte ;
                // 2. elle demandait au client de « signaler un changement » alors que RIEN
                //    ne stocke ce signalement chez nous (décision du 04/09, point 1 : la
                //    phrase ne revient que le jour où la route et la collection existent) ;
                // 3. « le retard est identifié de notre côté » se lit de deux façons, et
                //    c'est « la faute est chez nous » que le client retient. On dit donc ce
                //    que nous FAISONS (nous qualifions la cause) et ce qu'il a à faire (rien
                //    pour l'instant), sans dramatiser : un fichier sans mesure est d'abord
                //    un trou, pas un incident.
                //
                // ⚠️ Cette clé est TRANSITOIRE (dossier C2 §2.1) : elle porte le résidu
                // jusqu'à ce que les causes de flux soient armées côté enedis, puis sa copie
                // devient celle de `historique_collecte_interrompue`. Le texte, lui, survit.
                historique_retard_confirme:
                    "Le compteur {idsource} ne nous a plus transmis de mesures depuis le {derniere_mesure}, au-delà du délai que nous attendons pour ce compteur. Nous qualifions la cause de notre côté ; vous n'avez rien à faire pour l'instant. | Les compteurs {idsource} ne nous ont plus transmis de mesures depuis le {derniere_mesure}, au-delà du délai que nous attendons pour ces compteurs. Nous qualifions la cause de notre côté ; vous n'avez rien à faire pour l'instant.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent) : la
                // date ne peut pas être citée, et aucune valeur de repli ne la remplace
                // (même mécanique que `SCISSION_PAR_DONNEE` ailleurs dans ce fichier).
                historique_retard_confirme_sans_donnee:
                    "Le compteur {idsource} ne nous a pas encore transmis de mesures, au-delà du délai que nous attendons pour ce compteur. Nous qualifions la cause de notre côté ; vous n'avez rien à faire pour l'instant. | Les compteurs {idsource} ne nous ont pas encore transmis de mesures, au-delà du délai que nous attendons pour ces compteurs. Nous qualifions la cause de notre côté ; vous n'avez rien à faire pour l'instant.",
                // `historique_pas_de_maj_quotidienne_souscrite` (DEV-1624, décision de
                // Mathieu du 04/09, point 15 du dossier C2) : ce compteur n'a AUCUNE
                // souscription quotidienne active et aucune commande en cours. Lui servir
                // un « retard » était une erreur de fond, pas de style : il n'y a pas de
                // mise à jour quotidienne DUE, donc pas de retard à constater. Mesuré le
                // 04/09 sur les 132 `collecte_interrompue` à plus de 60 jours : 118 (89 %)
                // sont dans ce cas, majoritairement en régime EA ou ponctuel.
                //
                // ⚠️ Aucune action demandée : souscrire relève de NOTRE boucle, pas d'un
                // geste du client, et la copie ne promet pas cette souscription — rien dans
                // les champs servis ne dit qu'elle est prévue.
                historique_pas_de_maj_quotidienne_souscrite:
                    "Le compteur {idsource} n'a pas de mise à jour quotidienne souscrite ; les mesures reçues restent disponibles jusqu'au {derniere_mesure}. | Les compteurs {idsource} n'ont pas de mise à jour quotidienne souscrite ; les mesures reçues restent disponibles jusqu'au {derniere_mesure}.",
                // Variante SANS aucune donnée jamais reçue : la première phrase seule.
                historique_pas_de_maj_quotidienne_souscrite_sans_donnee:
                    "Le compteur {idsource} n'a pas de mise à jour quotidienne souscrite. | Les compteurs {idsource} n'ont pas de mise à jour quotidienne souscrite.",
                // Repli sur un `name` que cette version du front ne connaît pas
                // (`COPIE_REPLI_LIBELLE_INCONNU`). ⚠️ Ne peut pas être fausse : voir le bloc
                // de doc de la constante. Ne nomme personne, ne promet rien, n'affirme
                // aucune récupération en cours.
                data_retrieval_unknown_label:
                    "Une information sur la collecte du compteur {idsource} est disponible, mais cette version de l'application ne sait pas encore l'afficher. Contactez le support si vous avez besoin de précisions. | Une information sur la collecte des compteurs {idsource} est disponible, mais cette version de l'application ne sait pas encore l'afficher. Contactez le support si vous avez besoin de précisions.",
                // ⚠️ `cdc_activating_delayed` est RETIRÉ (revue du 31/08). Cette
                // copie n'était servie qu'aux PDL dont
                // `data_retrieval.historical.state` vaut `"delayed"` — valeur
                // qu'aucun écrivain n'émet depuis DEV-1324 : dapi
                // `_etat_historical()` (`modules/source/data_retrieval_enedis.py`,
                // sha 208975a3 lu le 31/08/2026) est son unique écrivain et ne
                // rend que `noconsent | finished | crashed | waiting`, ou `null`
                // en dégradation. `"delayed"` ne survit que dans l'énum pydantic
                // `StateWorkflowPdl`, qui documente un vocabulaire legacy. La
                // bannière ne portait donc JAMAIS ce cas : la garder aurait fait
                // passer du code mort pour une fonctionnalité livrée.
                // Blocages contractuels (DEV-1502, famille DEV-1460/1502/1503) : la donnée
                // n'arrive PAS et on NOMME la cause. ⚠️ Ne JAMAIS écrire ici « en cours de
                // récupération » ni promettre un délai — le backend a conclu l'inverse. Et
                // aucune formulation définitive (« définitivement », « plus jamais ») : ces
                // états se rouvrent d'eux-mêmes si le signal contractuel change côté Enedis.
                historique_pdl_hors_service:
                    "Le compteur {idsource} est résilié ou hors service auprès d'Enedis : Enedis ne publie plus de données pour ce point. Si le site est toujours occupé, un contrat d'électricité actif est nécessaire pour réactiver la collecte. | Les compteurs {idsource} sont résiliés ou hors service auprès d'Enedis : Enedis ne publie plus de données pour ces points. Si les sites sont toujours occupés, un contrat d'électricité actif est nécessaire pour réactiver la collecte.",
                // « L'historique déjà collecté reste disponible » n'est vraie QUE si
                // `verdict.last_data_date` est servi (cf. `aDejaRecuDesDonnees`,
                // même garde que `historique_souscription_absente`, DEV-1612).
                // Mesure A1 du 04/09 : 326 des 429 verdicts qui basculent n'ont jamais
                // reçu de donnée. Sans ce fait, la phrase est OMISE, jamais remplacée
                // par une autre promesse : voir la variante `_sans_donnee` plus bas.
                historique_consentement_absent:
                    "Nous n'avons pas (ou plus) d'autorisation valide pour collecter le compteur {idsource}. Renouvelez l'autorisation pour reprendre la collecte — l'historique déjà collecté reste disponible. | Nous n'avons pas (ou plus) d'autorisation valide pour collecter les compteurs {idsource}. Renouvelez l'autorisation pour reprendre la collecte — l'historique déjà collecté reste disponible.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent) :
                // même phrase, sans la promesse de disponibilité de l'historique.
                historique_consentement_absent_sans_donnee:
                    "Nous n'avons pas (ou plus) d'autorisation valide pour collecter le compteur {idsource}. Renouvelez l'autorisation pour reprendre la collecte. | Nous n'avons pas (ou plus) d'autorisation valide pour collecter les compteurs {idsource}. Renouvelez l'autorisation pour reprendre la collecte.",
                // ⚠️ SGT4H7 = opposition sincère du client : ce message décrit un état,
                // pas une panne.
                historique_souscription_cdc_absente:
                    "La courbe de charge n'est pas activée pour le compteur {idsource} (opposition ou service non souscrit auprès d'Enedis). Les autres données restent collectées. | La courbe de charge n'est pas activée pour les compteurs {idsource} (opposition ou service non souscrit auprès d'Enedis). Les autres données restent collectées.",
                // Owner `homeys` — émis par un lot api-metier parallèle (clé déclarée en
                // avance de phase, inerte tant que le backend ne l'envoie pas).
                historique_identifiant_inconnu:
                    "Le numéro de compteur {idsource} est inconnu d'Enedis. Vérifiez le numéro PRM saisi ou contactez le support pour corriger le rattachement. | Les numéros de compteur {idsource} sont inconnus d'Enedis. Vérifiez les numéros PRM saisis ou contactez le support pour corriger le rattachement.",
                // Owner `homeys` (DEV-1584) — le canal quotidien n'est pas armé de notre
                // côté. On NOMME le côté (le nôtre) et on dit ce qu'on sait, rien de plus :
                // ni reprise annoncée, ni délai (A4), ni « vous n'avez rien à faire »
                // (revue du 03/09 : aucun champ servi ne les soutient), ni mention
                // d'Enedis (ce retard n'est pas le sien). Le texte est épinglé en
                // littéral dans le spec.
                // ⚠️ Revue adversariale, seconde passe (03/09) : « Les données déjà
                // reçues restent disponibles » n'est vraie QUE si `verdict.last_data_date`
                // est servi. ~456 des 542 PRM visés (enedis#313, 03/09) n'ont JAMAIS
                // reçu de donnée — leur servir cette phrase répète, sous une forme
                // réduite, le défaut déjà jugé du blocant 1 (un littéral qui affirme
                // un fait sans lire aucun champ). D'où la scission ci-dessous, portée
                // par `aDejaRecuDesDonnees()` : ce libellé n'a plus qu'une phrase quand
                // `last_data_date` est absent.
                historique_souscription_absente:
                    "La mise à jour quotidienne du compteur {idsource} n'est pas encore armée de notre côté. Nous la relançons, et les données manquantes seront rattrapées. Les données déjà reçues restent disponibles. | La mise à jour quotidienne des compteurs {idsource} n'est pas encore armée de notre côté. Nous la relançons, et les données manquantes seront rattrapées. Les données déjà reçues restent disponibles.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent) :
                // même première phrase, sans la seconde — rien d'autre à dire de ce
                // qu'on ne détient pas.
                historique_souscription_absente_sans_donnee:
                    "La mise à jour quotidienne du compteur {idsource} n'est pas encore armée de notre côté. Nous la relançons, et les données manquantes seront rattrapées. | La mise à jour quotidienne des compteurs {idsource} n'est pas encore armée de notre côté. Nous la relançons, et les données manquantes seront rattrapées.",
                // Variante HORS CAPACITÉ (DEV-1584, validée par Mathieu le 04/09).
                // Servie quand api-metier lit `evidence.hors_capacite` : le dossier
                // Enedis du point refuse la souscription en soutirage, aucun job ne
                // commandera ce compteur. La copie NOMME le refus et sa source, et ne
                // relance rien — « nous la relançons » serait ici une promesse que la
                // machine refuse de tenir.
                // ⚠️ Aucun geste demandé au client : rien dans les champs servis ne dit
                // lequel serait utile. Aucune formulation définitive non plus (« jamais »,
                // « définitivement ») : le dossier Enedis d'un point peut changer.
                historique_souscription_absente_hors_capacite:
                    "D'après le dossier Enedis du point {idsource}, la mise à jour quotidienne en soutirage ne peut pas être souscrite. Les données déjà reçues restent disponibles. | D'après le dossier Enedis des points {idsource}, la mise à jour quotidienne en soutirage ne peut pas être souscrite. Les données déjà reçues restent disponibles.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent) :
                // la première phrase seule, comme pour le libellé nu.
                historique_souscription_absente_hors_capacite_sans_donnee:
                    "D'après le dossier Enedis du point {idsource}, la mise à jour quotidienne en soutirage ne peut pas être souscrite. | D'après le dossier Enedis des points {idsource}, la mise à jour quotidienne en soutirage ne peut pas être souscrite.",
                // 27/10 (SGT589) — textes validés par Mathieu, mot pour mot au singulier ;
                // le pluriel suit le même patron. Cause chez Enedis, aucun geste du
                // client, aucun délai. « l'historique déjà collecté reste disponible »
                // et la date ne sont servis que si `last_data_date` l'est : sinon la
                // variante `_sans_donnee` (`SCISSION_PAR_DONNEE`).
                historique_compteur_non_teleoperable:
                    "Le compteur {idsource} n'est actuellement pas téléopérable : Enedis ne peut pas le relever à distance et ne nous transmet plus ses mesures depuis le {derniere_mesure}. La collecte reprendra quand Enedis aura rétabli la télé-relève ; l'historique déjà collecté reste disponible. | Les compteurs {idsource} ne sont actuellement pas téléopérables : Enedis ne peut pas les relever à distance et ne nous transmet plus leurs mesures depuis le {derniere_mesure}. La collecte reprendra quand Enedis aura rétabli la télé-relève ; l'historique déjà collecté reste disponible.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent).
                historique_compteur_non_teleoperable_sans_donnee:
                    "Le compteur {idsource} n'est actuellement pas téléopérable : Enedis ne peut pas le relever à distance et ne nous a pas encore transmis de mesures. La collecte démarrera quand Enedis aura rétabli la télé-relève. | Les compteurs {idsource} ne sont actuellement pas téléopérables : Enedis ne peut pas les relever à distance et ne nous a pas encore transmis de mesures. La collecte démarrera quand Enedis aura rétabli la télé-relève.",
                // Repli honnête : sub_cause contractuelle inconnue de ce front. On dit le
                // blocage sans en inventer le détail, sans accuser personne et sans délai.
                historique_blocage_contractuel:
                    "La collecte du compteur {idsource} est actuellement bloquée au niveau du contrat ou des autorisations côté Enedis. Contactez le support pour identifier le blocage et reprendre la collecte. | La collecte des compteurs {idsource} est actuellement bloquée au niveau du contrat ou des autorisations côté Enedis. Contactez le support pour identifier le blocage et reprendre la collecte.",
                // INCIDENT INTERNE (DEV-1625, dossier C2 §2.3) — la cause est CHEZ NOUS.
                // Texte validé mot pour mot dans le dossier. Quatre choses qu'il fait, et
                // qu'aucune reformulation ne doit lui retirer :
                //  1. il NOMME le côté (le nôtre), sans jargon et sans accuser Enedis ;
                //  2. il DATE le fait que nous avons su AVANT le client (`{informe_le}`) —
                //     c'est la moitié de la règle de Mathieu, et elle est visible ;
                //  3. il dit ce qui RESTE disponible, parce que c'est ce que le client
                //     regarde d'abord ;
                //  4. il ferme par « vous n'avez rien à faire de votre côté » : la panne
                //     est à nous, et lui demander quoi que ce soit serait faux.
                // ⚠️ `{rattrapage}` est servi UNIQUEMENT si l'amont l'a mesuré. Aucune
                // valeur de repli : une phrase rassurante fausse est pire que son absence.
                // ⚠️ Aucune référence d'incident (§4 point 9), aucun « signalé à Enedis »
                // (aucun service d'incident n'existe au catalogue 0466 §1.3).
                historique_incident_homeys:
                    "La collecte du compteur {idsource} est interrompue de notre côté depuis le {depuis}. Notre équipe en a été informée le {informe_le} et l'incident est suivi chez nous. Les mesures reçues jusqu'au {derniere_mesure} restent disponibles dans l'application.{rattrapage} Vous n'avez rien à faire de votre côté. | La collecte des compteurs {idsource} est interrompue de notre côté depuis le {depuis}. Notre équipe en a été informée le {informe_le} et l'incident est suivi chez nous. Les mesures reçues jusqu'au {derniere_mesure} restent disponibles dans l'application.{rattrapage} Vous n'avez rien à faire de votre côté.",
                // Variante SANS aucune donnée jamais reçue (`last_data_date` absent) : la
                // phrase de disponibilité est RETIRÉE, et rien ne la remplace. Ni
                // « intactes » ni « aucune mesure » (cas DEV-1388 où la dataV2 a été
                // effacée : les deux seraient des affirmations qu'aucun champ ne soutient).
                historique_incident_homeys_sans_donnee:
                    "La collecte du compteur {idsource} est interrompue de notre côté depuis le {depuis}. Notre équipe en a été informée le {informe_le} et l'incident est suivi chez nous.{rattrapage} Vous n'avez rien à faire de votre côté. | La collecte des compteurs {idsource} est interrompue de notre côté depuis le {depuis}. Notre équipe en a été informée le {informe_le} et l'incident est suivi chez nous.{rattrapage} Vous n'avez rien à faire de votre côté.",
                // Variante PORTÉE PARC : notre réception entière est interrompue. La
                // phrase ne nomme AUCUN compteur (l'incident n'est pas le leur) et se
                // borne aux compteurs relevés quotidiennement — le parc R6X, pas « tous
                // vos compteurs ». Singulier et pluriel identiques : elle ne cite personne.
                historique_incident_homeys_parc:
                    "Notre réception des mesures quotidiennes d'Enedis est interrompue depuis le {depuis} pour les compteurs relevés quotidiennement. Notre équipe en a été informée le {informe_le}. Les mesures déjà reçues restent disponibles.{rattrapage} Vous n'avez rien à faire de votre côté. | Notre réception des mesures quotidiennes d'Enedis est interrompue depuis le {depuis} pour les compteurs relevés quotidiennement. Notre équipe en a été informée le {informe_le}. Les mesures déjà reçues restent disponibles.{rattrapage} Vous n'avez rien à faire de votre côté.",
                historique_incident_homeys_parc_sans_donnee:
                    "Notre réception des mesures quotidiennes d'Enedis est interrompue depuis le {depuis} pour les compteurs relevés quotidiennement. Notre équipe en a été informée le {informe_le}.{rattrapage} Vous n'avez rien à faire de votre côté. | Notre réception des mesures quotidiennes d'Enedis est interrompue depuis le {depuis} pour les compteurs relevés quotidiennement. Notre équipe en a été informée le {informe_le}.{rattrapage} Vous n'avez rien à faire de votre côté.",

                // ── TROU CONNU CHEZ ENEDIS (DEV-1650, décision du 07/09) ─────
                // Texte SOUMIS À `/texte-ok` de Mathieu, écrit sur sa décision :
                // « dire au client qu'il y a un trou chez enedis connu pour pas
                // qu'il écrive au support ». La dernière phrase (« Les autres
                // mesures sont disponibles ») n'est vraie QUE si le compteur a
                // reçu de la donnée : d'où la scission `_sans_donnee` (même
                // mécanique que DEV-1584/1612/1625), et non une valeur de repli.
                // `{periode}` et `{constat}` sont des fragments composés plus bas
                // (`variablesDeLAbsence`) : la période avec ou sans « par
                // périodes », et la PROVENANCE du constat.
                historique_absence_instruite:
                    "Pour le compteur {idsource}, les mesures {periode} manquent chez Enedis : trou connu, constaté le {constate_le} {constat}. Nous ne les redemandons pas ; si elles apparaissent, nous les intégrerons. Les autres mesures sont disponibles. | Pour les compteurs {idsource}, les mesures {periode} manquent chez Enedis : trou connu, constaté le {constate_le} {constat}. Nous ne les redemandons pas ; si elles apparaissent, nous les intégrerons. Les autres mesures sont disponibles.",
                // Variante SANS aucune donnée jamais reçue : la dernière phrase est
                // RETIRÉE et rien ne la remplace — ni « aucune mesure », ni une
                // autre promesse (cas DEV-1388).
                historique_absence_instruite_sans_donnee:
                    "Pour le compteur {idsource}, les mesures {periode} manquent chez Enedis : trou connu, constaté le {constate_le} {constat}. Nous ne les redemandons pas ; si elles apparaissent, nous les intégrerons. | Pour les compteurs {idsource}, les mesures {periode} manquent chez Enedis : trou connu, constaté le {constate_le} {constat}. Nous ne les redemandons pas ; si elles apparaissent, nous les intégrerons.",
                // Les FRAGMENTS de la copie ci-dessus. Ils existent séparément pour
                // qu'une combinaison (période bornée ou non × plages contiguës ou
                // disjointes × trois provenances) ne fasse pas six clés de copie.
                absence_instruite_periode: "du {du} au {au}",
                absence_instruite_periode_par_periodes:
                    "du {du} au {au}, par périodes",
                // Plage ouverte sur le passé (`depuis` absent) : on ne fabrique pas
                // une date de début qu'Enedis n'a jamais bornée.
                absence_instruite_periode_sans_debut: "jusqu'au {au}",
                // La PROVENANCE, dans les mots du client. Jamais « par écrit » : sur
                // un trou constaté, personne chez Enedis n'a rien écrit.
                absence_instruite_constat_commande:
                    "à la réception de notre commande d'historique",
                absence_instruite_constat_consultation:
                    "par consultation directe",
                // `cr_terminal` : là, Enedis a bien répondu à notre commande. La
                // formulation reste factuelle et ne cite aucun motif SGE.
                absence_instruite_constat_reponse:
                    "en réponse à notre commande",

                // ── AVANCEMENT (décisions A1-A4 du 25/08, DEV-1499) ──────────
                // Phrases AJOUTÉES sous la copie ci-dessus quand le verdict porte
                // de quoi les écrire. Elles répondent à « où j'en suis ? », que
                // les copies de base laissaient sans réponse.
                //
                // ⚠️ Trois objets distincts (A2) : la FRAÎCHEUR (de quand date la
                // dernière mesure), le STOCK (ce qu'on détient, onglet « Données
                // reçues ») et la DURÉE DE MISE À DISPOSITION (clic → données dans
                // l'app). `avancement_eta*` parle de la 3ᵉ, `avancement_*mesure` de
                // la 1ʳᵉ. Ne jamais mélanger les deux dans une même phrase.

                // Une demande court : on annonce l'échéance, GELÉE à l'émission
                // (A1) et plafonnée à l'engagement Enedis de 72 h (A2). On dit
                // « le plus souvent » et non une durée promise : la médiane
                // observée est de l'ordre de l'heure, la queue va plus loin.
                avancement_eta:
                    "Demande envoyée à Enedis le {emis_le}. Les données arrivent le plus souvent en quelques heures, et au plus tard le {echeance} — c'est le délai maximum annoncé par Enedis.",
                // Échéance dépassée : on DIT le retard, et rien d'autre (A4 — aucun
                // détail sur notre système, aucune nouvelle date promise).
                avancement_eta_depassee:
                    "La demande envoyée le {emis_le} a dépassé le délai annoncé : il y a du retard. Ce retard est identifié de notre côté, vous n'avez rien à faire.",
                // Axe FRAÎCHEUR : l'attendu vient du verdict (`expected_lag_days`,
                // résolu par compteur côté enedis) — jamais d'un seuil du front.
                // « nous attendons » et non « vous les aurez » : c'est une attente,
                // pas un engagement.
                avancement_prochaine_mesure:
                    "Dernière mesure reçue : le {derniere_mesure}. Pour ce compteur, nous attendons les suivantes d'ici le {echeance}.",
                // Le FAIT nu, sans échéance : soit le retard est confirmé (le flux a
                // décroché), soit l'échéance attendue est déjà passée. Dans les deux
                // cas, annoncer une date serait promettre ce qu'on ne tient pas.
                // DEV-1581 (décision A11 du 03/09), chaîne cliente rouverte par
                // DEV-1632. Le client lit QUEL flux manque, DEPUIS QUAND, et ce que
                // NOUS faisons.
                //
                // ⚠️ TROIS phrases interdites ici, et chacune a été écrite une fois :
                // 1. « Ce retard est identifié de notre côté » (PRs #499/#500, refus de
                //    Mathieu du 04/09) : un flux qu'Enedis ne dépose plus est un retard
                //    CHEZ ENEDIS. Dire « de notre côté » désigne le mauvais responsable
                //    et transforme un fait en excuse.
                // 2. « en cours de récupération » (A4) : rien n'est en cours tant que
                //    le verdict ne porte pas de commande.
                //  3. une impasse — une bannière qui constate sans dire ce que nous
                //    faisons. D'où `{action}`, servie dans les DEUX cas.
                //
                // `{flux}` et `{action}` sont composés plus bas à partir des faits du
                // verdict : jamais un mot qui ne soit porté par un champ servi.
                [LIBELLE_STREAM_EN_RETARD]:
                    "Une partie des mesures du compteur {idsource} ne nous parvient plus alors que le reste continue d'arriver : {flux}. {action} Vous n'avez rien à faire de votre côté. | Une partie des mesures des compteurs {idsource} ne nous parvient plus alors que le reste continue d'arriver : {flux}. {action} Vous n'avez rien à faire de votre côté.",
                // Aucun des flux en retard n'a JAMAIS été reçu : « ne nous parvient
                // plus » serait faux (rien n'a cessé, rien n'a commencé). Un seul mot
                // change, et c'est le seul écart au texte proposé le 04/09 — à valider.
                [LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU]:
                    "Une partie des mesures du compteur {idsource} ne nous parvient pas alors que le reste continue d'arriver : {flux}. {action} Vous n'avez rien à faire de votre côté. | Une partie des mesures des compteurs {idsource} ne nous parvient pas alors que le reste continue d'arriver : {flux}. {action} Vous n'avez rien à faire de votre côté.",
                // Les fragments de `{flux}` : un par flux, puis le délai.
                stream_en_retard_flux_date:
                    "{nom}, dernière réception le {depuis}",
                stream_en_retard_flux_jamais:
                    "{nom} n'a encore jamais été reçue",
                stream_en_retard_delai:
                    "au-delà du délai que nous attendons pour ce flux | au-delà du délai que nous attendons pour ces flux",
                // `{action}`. La première n'est servie QUE si le verdict porte une
                // commande de série en vol (`recommande_en_vol === true`) : sans ce
                // fait, dire « nous la redemandons » promettrait un appel que la
                // machine ne fait pas. La seconde est vraie dans tous les cas — le flux
                // est dans la table des attendus, le battement le compte chaque jour.
                stream_en_retard_action_recommande:
                    "Nous la redemandons à Enedis. | Nous les redemandons à Enedis.",
                stream_en_retard_action_suivi:
                    "Nous suivons ce flux. | Nous suivons ces flux.",

                // ── COMMANDE NON PASSÉE : le retard est LE NÔTRE (DEV-1661) ──
                // Texte SOUMIS À `/texte-ok` de Mathieu. Le fait (enedis#350) : tous
                // les flux dus en retard de ce compteur sont des commandes M023 dont
                // la dernière est close et que personne n'a repassées (budget
                // d'affaires saturé, ou planificateur). Ce que la copie fait :
                //  1. elle NOMME le côté (le nôtre) : avant, le client lisait « Nous
                //     la redemandons à Enedis » sur une commande jamais partie ;
                //  2. elle nomme le flux, date sa dernière réception et notre
                //     dernière demande, depuis les champs servis et rien d'autre ;
                //  3. elle dit ce que nous FAISONS (la demande part avec notre
                //     prochain lot), SANS date : aucun champ servi ne porte la date
                //     du prochain lot, on n'en fabrique pas ;
                //  4. elle rassure : les autres mesures ne sont pas concernées, vrai
                //     PAR CONSTRUCTION du verdict (tout autre flux dû est à l'heure),
                //     mais seulement si une mesure a déjà été reçue, d'où la
                //     scission `_sans_donnee`.
                // ⚠️ Jamais « redemandons à Enedis », « écrit à Enedis », ni un mot
                // de budget ou de planificateur : le client n'a rien à en faire.
                [LIBELLE_COMMANDE_NON_PASSEE]:
                    "Le retard du compteur {idsource} vient de notre côté : {flux}. {action} Vos autres mesures ne sont pas concernées. | Le retard des compteurs {idsource} vient de notre côté : {flux}. {action} Vos autres mesures ne sont pas concernées.",
                // Variante SANS aucune donnée jamais reçue : la phrase de rassurance
                // est RETIRÉE et rien ne la remplace.
                [LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE]:
                    "Le retard du compteur {idsource} vient de notre côté : {flux}. {action} | Le retard des compteurs {idsource} vient de notre côté : {flux}. {action}",
                // Les fragments de `{flux}`, un par flux. `{reception}` est composé
                // plus bas (`variablesDesCommandes`) : « (dernière réception le …) »
                // ou RIEN quand le flux n'a jamais été reçu. La date de notre
                // dernière demande n'est citée que si elle est servie ; sinon la
                // phrase courte, jamais une date de repli.
                commande_non_passee_flux_depuis:
                    "la demande d'historique pour {nom}{reception} n'a pas encore été repassée à Enedis depuis notre dernière demande du {derniere_demande}",
                commande_non_passee_flux:
                    "la demande d'historique pour {nom}{reception} n'a pas encore été passée à Enedis",
                commande_non_passee_reception:
                    "(dernière réception le {depuis})",
                // Aucun flux servi avec le libellé (ne devrait pas arriver : le
                // verdict exige un retard) : la phrase reste vraie et lisible.
                commande_non_passee_flux_sans_detail:
                    "une demande d'historique n'a pas encore été passée à Enedis",
                // `{action}`, au pluriel des flux. Sans date : cf. point 3.
                commande_non_passee_action:
                    "Elle part avec notre prochain lot de demandes. | Elles partent avec notre prochain lot de demandes.",
                avancement_derniere_mesure:
                    "Dernière mesure reçue : le {derniere_mesure}, il y a {lag_days} jour. | Dernière mesure reçue : le {derniere_mesure}, il y a {lag_days} jours.",
            },
        },
    })

    const {
        $getAllEnedisWorkflowByBuildingId,
        $getAllEnedisWorkflowBySourceId,
    } = inject<Services>("services")!

    const workflowEnedisList = ref<WorkflowEnedis[]>([])

    /**
     * Récupère les workflows Enedis pour un bâtiment ou une source
     * en fonction du type. Récupère l'identifiant dans le store graph
     */
    const { fetch, isFetching, hasError } = useFetchWrapper(async () => {
        const service =
            type === "building"
                ? $getAllEnedisWorkflowByBuildingId
                : $getAllEnedisWorkflowBySourceId

        const id =
            type === "building"
                ? buildingStore.building?._id
                : graphStore.currentSourceId

        try {
            workflowEnedisList.value = await service(id!)
        } catch (err) {
            Sentry.captureException(err, {
                extra: {
                    type,
                    id,
                },
            })
        }
    })

    /** Sources du périmètre courant (bâtiment entier, ou la source affichée). */
    const sourcesDuPerimetre = computed<Source[]>(() => {
        const candidates: (Source | undefined)[] =
            type === "building"
                ? buildingStore.getSources
                : [
                      graphStore.currentSourceId
                          ? sourceStore.getSourceById(
                                graphStore.currentSourceId
                            )
                          : undefined,
                  ]
        return candidates.filter((source): source is Source => !!source)
    })

    /**
     * PRM → horodatage d'ÉMISSION de la commande en vol.
     *
     * ⚠️ Ce fait n'est PAS dans la traçabilité servie par api-metier
     * (`traceDuVerdict` porte `inflight_n`, pas `inflight_since`). Il l'est dans
     * la source elle-même : dapi expose `inflight_since` du verdict en
     * `informations.pdl.data_retrieval.historical.created_at`
     * (`data_retrieval_enedis.py`). On le joint ici par `idsource` plutôt que
     * d'inventer un champ ou de figer une date côté client — une échéance gelée
     * doit l'être sur un FAIT serveur, sinon elle repart à zéro à chaque
     * rechargement de page et le retard ne se voit jamais (A1).
     */
    const emissionParIdsource = computed<Record<string, string | Date>>(() => {
        const parPrm: Record<string, string | Date> = {}
        for (const source of sourcesDuPerimetre.value) {
            const emisLe =
                source?.informations?.pdl?.data_retrieval?.historical
                    ?.created_at
            if (source?.idsource && emisLe) parPrm[source.idsource] = emisLe
        }
        return parPrm
    })

    /**
     * Phrase d'avancement d'un workflow, déjà traduite — ou `null`.
     * @param workflow - Élément de workflow servi par api-metier
     * @param maintenant - Instant de l'affichage
     * @returns `{cle, texte}` où `cle` sert à ne grouper que des avancements
     *   IDENTIQUES (deux compteurs commandés à des heures différentes ne peuvent
     *   pas partager une échéance), ou `null`
     */
    const avancementTraduit = (
        workflow: WorkflowEnedis,
        maintenant: Date
    ): { cle: string; texte: string } | null => {
        const avancement: AvancementEnedis | null = avancementEnedis(
            workflow.name,
            workflow.verdict,
            emissionParIdsource.value[workflow.idsource] ?? null,
            maintenant
        )
        if (!avancement) return null

        switch (avancement.key) {
            case "avancement_eta":
                return {
                    cle: `${avancement.key}|${avancement.echeance.toISOString()}`,
                    texte: t(avancement.key, {
                        emis_le: formatDate(avancement.emisLe, FORMAT_ECHEANCE),
                        echeance: formatDate(
                            avancement.echeance,
                            FORMAT_ECHEANCE
                        ),
                    }),
                }
            case "avancement_eta_depassee":
                return {
                    cle: `${avancement.key}|${avancement.emisLe.toISOString()}`,
                    texte: t(avancement.key, {
                        emis_le: formatDate(avancement.emisLe, FORMAT_ECHEANCE),
                    }),
                }
            case "avancement_prochaine_mesure":
                return {
                    cle: `${avancement.key}|${avancement.derniereMesure.toISOString()}|${avancement.echeance.toISOString()}`,
                    texte: t(avancement.key, {
                        derniere_mesure: formatDate(avancement.derniereMesure),
                        echeance: formatDate(avancement.echeance),
                    }),
                }
            case "avancement_derniere_mesure":
                return {
                    cle: `${avancement.key}|${avancement.derniereMesure.toISOString()}`,
                    texte: t(
                        avancement.key,
                        {
                            derniere_mesure: formatDate(
                                avancement.derniereMesure
                            ),
                            lag_days: avancement.lagDays,
                        },
                        avancement.lagDays
                    ),
                }
        }
    }

    /**
     * Récupère les messages des workflows Enedis (texte + canal visuel).
     */
    const getWorkflowEnedisMessageList = computed<
        WorkflowEnedisBannerMessage[]
    >(() => {
        const messages: WorkflowEnedisBannerMessage[] = []
        const maintenant = new Date()

        /**
         * Regroupement par LIBELLÉ **et** par avancement.
         *
         * Le groupe était le seul `name` : deux compteurs partageant un libellé
         * partagent une phrase. Ça reste vrai de la copie de base, mais PAS de
         * l'avancement — deux demandes émises à des heures différentes n'ont pas
         * la même échéance, et les fondre en une seule phrase en inventerait une.
         * D'où la clé composite : même libellé + même avancement (aux dates près)
         * ⇒ un seul message ; sinon un message par échéance distincte. Les
         * workflows sans avancement gardent EXACTEMENT le groupement d'avant.
         *
         * ⚠️ Depuis DEV-1624, la clé porte AUSSI la date de dernière mesure pour les
         * libellés dont la copie la CITE (`CLES_QUI_CITENT_LA_DERNIERE_MESURE`) : le
         * défaut est le même que celui de l'échéance. Deux compteurs décrochés à des
         * dates différentes fondus en un seul message auraient partagé UNE date,
         * fausse pour l'un des deux — une phrase qui cite un fait doit être groupée
         * sur ce fait.
         */
        const groupes = workflowEnedisList.value.reduce(
            (acc, workflow) => {
                const avancement = avancementTraduit(workflow, maintenant)
                const dateCitee = CLES_QUI_CITENT_LA_DERNIERE_MESURE.has(
                    workflow.name
                )
                    ? (workflow.verdict?.last_data_date ?? "")
                    : ""
                // DEV-1625 : les copies d'incident citent DEUX dates de plus
                // (`{depuis}`, `{informe_le}`) et servent ou non `{rattrapage}`.
                // Ces trois faits entrent dans la clé, sinon deux compteurs aux
                // faits différents partageraient une bannière qui n'en dit qu'un.
                const inc = workflow.verdict?.incident
                const signatureIncident = LIBELLES_INCIDENT.has(workflow.name)
                    ? `${inc?.depuis ?? ""}::${inc?.notifie_le ?? ""}::${
                          inc?.rattrapage_automatique === true ? "1" : "0"
                      }`
                    : ""
                // DEV-1632 : la copie A11 cite les flux ET l'état de la
                // re-commande. Deux compteurs qui ne partagent pas ces faits ne
                // partagent pas la bannière (même défaut que la date ci-dessus).
                const fluxCites = CLES_QUI_CITENT_LES_FLUX.has(workflow.name)
                    ? `${signatureDesFlux(workflow.verdict?.streams_en_retard)}@@${String(
                          workflow.verdict?.recommande_en_vol ?? "null"
                      )}`
                    : ""
                // DEV-1650 : la copie du trou connu cite une PÉRIODE, une DATE de
                // constat et une PROVENANCE. Deux compteurs dont les trous ne
                // tombent pas aux mêmes dates, ou n'ont pas été constatés de la
                // même façon, ne partagent pas la bannière — elle serait fausse
                // pour l'un des deux (même défaut que la date et que les flux).
                const abs = workflow.verdict?.absence_instruite
                const signatureAbsence = CLES_QUI_CITENT_LABSENCE.has(
                    workflow.name
                )
                    ? `${abs?.depuis ?? ""}::${abs?.jusqu_au ?? ""}::${
                          abs?.constate_le ?? ""
                      }::${(abs?.provenances ?? []).join(",")}::${
                          (abs?.n_plages ?? 1) > 1 ? "n" : "1"
                      }`
                    : ""
                // DEV-1661 : la copie de la commande non passée cite, par flux, le
                // nom, la dernière réception et notre dernière demande. Même règle.
                const commandesCitees = CLES_QUI_CITENT_LES_COMMANDES.has(
                    workflow.name
                )
                    ? signatureDesCommandes(workflow.verdict?.streams_en_retard)
                    : ""
                // Les quatre signatures cohabitent : les libellés d'incident, le
                // libellé A11, celui de l'absence et celui de la commande sont
                // disjoints, donc trois des quatre valent toujours "".
                const cle = `${workflow.name}::${dateCitee}::${fluxCites}::${avancement?.cle ?? ""}::${signatureIncident}::${signatureAbsence}::${commandesCitees}`
                if (!acc[cle]) {
                    acc[cle] = {
                        name: workflow.name,
                        avancement: avancement?.texte ?? null,
                        workflows: [],
                    }
                }
                acc[cle].workflows.push(workflow)
                return acc
            },
            {} as Record<
                string,
                {
                    name: string
                    avancement: string | null
                    workflows: WorkflowEnedis[]
                }
            >
        )

        /** Idsources DÉDOUBLONNÉS et formatés — la longueur pilote le pluriel des copies. */
        const distinctIdsources = (workflows: WorkflowEnedis[]) =>
            workflows
                .map((w) => w.idsource)
                .filter((v, i, a) => a.indexOf(v) === i)
                .map((idsource) => formatIdSource(idsource, SOURCES_TYPES.PDL))

        /**
         * Une mesure a-t-elle DÉJÀ été reçue pour ce workflow ? Seul champ qui
         * peut soutenir « les données déjà reçues restent disponibles » —
         * `last_data_date` absent ⇒ pas de phrase, jamais une valeur de repli
         * (même garde que l'avancement, revue adversariale seconde passe,
         * DEV-1584).
         */
        const aDejaRecuDesDonnees = (workflow: WorkflowEnedis): boolean =>
            !!workflow.verdict?.last_data_date

        /**
         * Libellés dont la copie contient une phrase qui n'est vraie QUE si le
         * compteur a déjà reçu une donnée (`aDejaRecuDesDonnees`) : le sort du
         * client (donnée déjà reçue ou non) ne se mélange pas dans un même
         * message, sous peine d'affirmer une disponibilité à un PRM qui n'en a
         * jamais eu. Général plutôt qu'un empilement de cas particuliers : tout
         * nouveau libellé qui promet la même chose s'ajoute ici, une ligne,
         * sans toucher à la boucle plus bas (DEV-1612, même mécanique que
         * DEV-1584).
         */
        const SCISSION_PAR_DONNEE: Record<
            string,
            { avecDonnee: string; sansDonnee: string }
        > = {
            [LIBELLE_SOUSCRIPTION_ABSENTE]: {
                avecDonnee: LIBELLE_SOUSCRIPTION_ABSENTE,
                sansDonnee: LIBELLE_SOUSCRIPTION_ABSENTE_SANS_DONNEE,
            },
            [LIBELLE_CONSENTEMENT_ABSENT]: {
                avecDonnee: LIBELLE_CONSENTEMENT_ABSENT,
                sansDonnee: LIBELLE_CONSENTEMENT_ABSENT_SANS_DONNEE,
            },
            [LIBELLE_SOUSCRIPTION_ABSENTE_HORS_CAPACITE]: {
                avecDonnee: LIBELLE_SOUSCRIPTION_ABSENTE_HORS_CAPACITE,
                sansDonnee:
                    LIBELLE_SOUSCRIPTION_ABSENTE_HORS_CAPACITE_SANS_DONNEE,
            },
            // DEV-1624 : ces deux copies CITENT la date de dernière mesure. Sans
            // `last_data_date`, la phrase ne se répare pas par une valeur de repli,
            // elle se remplace par une variante qui ne cite rien.
            // DEV-1625 : la copie d'incident affirme « les mesures reçues jusqu'au …
            // restent disponibles ». Sans `last_data_date`, la phrase est RETIRÉE et
            // rien ne la remplace (ni « intactes », ni « aucune mesure » — cas DEV-1388).
            [LIBELLE_INCIDENT_HOMEYS]: {
                avecDonnee: LIBELLE_INCIDENT_HOMEYS,
                sansDonnee: LIBELLE_INCIDENT_HOMEYS_SANS_DONNEE,
            },
            [LIBELLE_INCIDENT_HOMEYS_PARC]: {
                avecDonnee: LIBELLE_INCIDENT_HOMEYS_PARC,
                sansDonnee: LIBELLE_INCIDENT_HOMEYS_PARC_SANS_DONNEE,
            },
            [LIBELLE_RETARD_CONFIRME]: {
                avecDonnee: LIBELLE_RETARD_CONFIRME,
                sansDonnee: LIBELLE_RETARD_CONFIRME_SANS_DONNEE,
            },
            [LIBELLE_PAS_DE_MAJ_QUOTIDIENNE]: {
                avecDonnee: LIBELLE_PAS_DE_MAJ_QUOTIDIENNE,
                sansDonnee: LIBELLE_PAS_DE_MAJ_QUOTIDIENNE_SANS_DONNEE,
            },
            // 27/10 (SGT589) : la copie cite la date ET promet l'historique déjà
            // collecté. Sans `last_data_date`, ni l'un ni l'autre.
            [LIBELLE_COMPTEUR_NON_TELEOPERABLE]: {
                avecDonnee: LIBELLE_COMPTEUR_NON_TELEOPERABLE,
                sansDonnee: LIBELLE_COMPTEUR_NON_TELEOPERABLE_SANS_DONNEE,
            },
            // DEV-1650 : « Les autres mesures sont disponibles » n'est vraie que si
            // le compteur a reçu de la donnée. Sans `last_data_date`, la phrase est
            // retirée et rien ne la remplace.
            [LIBELLE_ABSENCE_INSTRUITE]: {
                avecDonnee: LIBELLE_ABSENCE_INSTRUITE,
                sansDonnee: LIBELLE_ABSENCE_INSTRUITE_SANS_DONNEE,
            },
            // DEV-1661 : « Vos autres mesures ne sont pas concernées » n'a de sens
            // que si une mesure a déjà été reçue. Sans `last_data_date`, la phrase
            // est retirée et rien ne la remplace.
            [LIBELLE_COMMANDE_NON_PASSEE]: {
                avecDonnee: LIBELLE_COMMANDE_NON_PASSEE,
                sansDonnee: LIBELLE_COMMANDE_NON_PASSEE_SANS_DONNEE,
            },
        }

        /**
         * La variante de copie d'un libellé, quand elle dépend d'un fait du verdict
         * (DEV-1632). Le groupe est homogène sur ce fait par construction (il fait
         * partie de la clé de regroupement) : le premier workflow le porte pour tous.
         *
         * @param name - Libellé servi par api-metier
         * @param workflow - N'importe quel workflow du groupe
         * @returns La clé i18n à servir
         */
        const varianteDuLibelle = (
            name: string,
            workflow: WorkflowEnedis
        ): string => {
            if (name !== LIBELLE_STREAM_EN_RETARD) return name
            const flux = fluxEnRetard(workflow.verdict?.streams_en_retard)
            // AUCUN flux jamais reçu ⇒ « ne nous parvient plus » serait faux.
            return flux.length > 0 && flux.every((f) => !f.depuis)
                ? LIBELLE_STREAM_EN_RETARD_JAMAIS_RECU
                : name
        }

        /**
         * Les variables `{flux}` et `{action}` de la copie A11 (DEV-1632), ou `{}`.
         *
         * `{flux}` : un fragment par flux, dans l'ordre stable servi par enedis, puis
         * le délai attendu. `{action}` : « nous la redemandons » UNIQUEMENT si le
         * verdict porte une commande de série en vol (`recommande_en_vol === true`) —
         * `false` ET `null` (fait non lu) servent « nous suivons ce flux ». On ne
         * promet pas un appel sur un fait qu'on n'a pas lu.
         *
         * @param name - Libellé servi par api-metier
         * @param workflow - N'importe quel workflow du groupe (homogène par la clé)
         * @returns Les variables à interpoler
         */
        const variablesDesFlux = (
            name: string,
            workflow: WorkflowEnedis
        ): Record<string, string> => {
            if (!CLES_QUI_CITENT_LES_FLUX.has(name)) return {}
            const flux = fluxEnRetard(workflow.verdict?.streams_en_retard)
            if (flux.length === 0) return {}
            const fragments = flux.map((f) =>
                f.depuis
                    ? t("stream_en_retard_flux_date", {
                          nom: f.nom,
                          depuis: formatDate(f.depuis),
                      })
                    : t("stream_en_retard_flux_jamais", { nom: f.nom })
            )
            const delai = t("stream_en_retard_delai", {}, flux.length)
            const action =
                workflow.verdict?.recommande_en_vol === true
                    ? t("stream_en_retard_action_recommande", {}, flux.length)
                    : t("stream_en_retard_action_suivi", {}, flux.length)
            return { flux: `${fragments.join(" ; ")}, ${delai}`, action }
        }

        /**
         * Les variables `{periode}`, `{constat}` et `{constate_le}` de la copie du
         * trou connu (DEV-1650), ou `{}` si le libellé ne les cite pas.
         *
         * Trois choix, et chacun a sa raison :
         * - `{periode}` — « du … au … » ; « , par périodes » quand plusieurs plages
         *   sont actives (`n_plages > 1`), parce qu'une paire de dates seule laisserait
         *   croire à un trou continu ; « jusqu'au … » quand la plage est ouverte sur le
         *   passé (`depuis` absent) — on ne fabrique pas une date de début.
         * - `{constat}` — la PROVENANCE, dans les mots du client. `consultation_detaillee`
         *   dit « par consultation directe » ; `historique_commande` dit « à la réception
         *   de notre commande d'historique » ; `cr_terminal` dit « en réponse à notre
         *   commande ». Une provenance NON LUE (`null`, interrupteur enedis désarmé)
         *   retombe sur la formulation la plus prudente, celle du CR.
         * - `{constate_le}` — la date du constat CHEZ NOUS, jamais servie sous
         *   « Enedis a clôturé le ».
         *
         * Le groupe est homogène sur ces faits par construction (ils font partie de la
         * clé de regroupement) : le premier workflow les porte pour tous.
         *
         * @param name - Libellé servi par api-metier
         * @param workflow - N'importe quel workflow du groupe
         * @returns Les variables à interpoler
         */
        const variablesDeLAbsence = (
            name: string,
            workflow: WorkflowEnedis
        ): Record<string, string> => {
            if (!CLES_QUI_CITENT_LABSENCE.has(name)) return {}
            const absence = workflow.verdict?.absence_instruite
            if (!absence?.jusqu_au) return {}
            const periode = absence.depuis
                ? t(
                      (absence.n_plages ?? 1) > 1
                          ? "absence_instruite_periode_par_periodes"
                          : "absence_instruite_periode",
                      {
                          du: formatDate(absence.depuis),
                          au: formatDate(absence.jusqu_au),
                      }
                  )
                : t("absence_instruite_periode_sans_debut", {
                      au: formatDate(absence.jusqu_au),
                  })
            const provenances = absence.provenances ?? []
            const constat = provenances.includes("consultation_detaillee")
                ? t("absence_instruite_constat_consultation")
                : provenances.includes("historique_commande")
                  ? t("absence_instruite_constat_commande")
                  : t("absence_instruite_constat_reponse")
            return {
                periode,
                constat,
                constate_le: formatDate(absence.constate_le ?? ""),
            }
        }

        /**
         * Les variables `{flux}` et `{action}` de la copie de la commande non passée
         * (DEV-1661), ou `{}` si le libellé ne les cite pas.
         *
         * `{flux}` : un fragment par flux, dans l'ordre stable servi par enedis. Chaque
         * fragment nomme le flux, cite sa dernière réception SI elle est servie (rien
         * sinon : un flux jamais reçu n'a pas de date), et date notre dernière demande
         * SI elle est servie (`derniere_commande.commanded_at`) ; sans elle, la phrase
         * courte, jamais une date de repli. `{action}` : « part avec notre prochain
         * lot », au pluriel des flux, sans date (aucun champ ne la porte).
         *
         * Le groupe est homogène sur ces faits par construction (ils font partie de la
         * clé de regroupement) : le premier workflow les porte pour tous.
         *
         * @param name - Clé de copie servie (libellé ou sa variante `_sans_donnee`)
         * @param workflow - N'importe quel workflow du groupe
         * @returns Les variables à interpoler
         */
        const variablesDesCommandes = (
            name: string,
            workflow: WorkflowEnedis
        ): Record<string, string> => {
            if (!CLES_QUI_CITENT_LES_COMMANDES.has(name)) return {}
            const flux = fluxCommandesNonPassees(
                workflow.verdict?.streams_en_retard
            )
            if (flux.length === 0) {
                return {
                    flux: t("commande_non_passee_flux_sans_detail"),
                    action: t("commande_non_passee_action", {}, 1),
                }
            }
            const fragments = flux.map((f) => {
                const reception = f.depuis
                    ? ` ${t("commande_non_passee_reception", {
                          depuis: formatDate(f.depuis),
                      })}`
                    : ""
                return f.derniereDemandeLe
                    ? t("commande_non_passee_flux_depuis", {
                          nom: f.nom,
                          reception,
                          derniere_demande: formatDate(f.derniereDemandeLe),
                      })
                    : t("commande_non_passee_flux", { nom: f.nom, reception })
            })
            return {
                flux: fragments.join(" ; "),
                action: t("commande_non_passee_action", {}, flux.length),
            }
        }

        const unknownWorkflows: WorkflowEnedis[] = []

        for (const { name, avancement, workflows } of Object.values(groupes)) {
            // Silence CHOISI avant tout le reste (08/09) : ces libellés sont connus,
            // donc ils ne partent ni en bannière dédiée, ni au repli « libellé
            // inconnu », ni en alerte Sentry. Cf. `LIBELLES_SANS_BANNIERE`.
            if (LIBELLES_SANS_BANNIERE.has(name)) continue
            const severity = WORKFLOW_ENEDIS_DEDICATED_COPY[name]
            if (!severity) {
                unknownWorkflows.push(...workflows)
                continue
            }

            // Les libellés de `SCISSION_PAR_DONNEE` scindent leur groupe en deux
            // textes : le sort du client (donnée déjà reçue ou non) ne se
            // mélange pas dans un même message, sous peine d'affirmer une
            // disponibilité de l'historique à un PRM qui n'en a jamais reçu.
            const scission = SCISSION_PAR_DONNEE[name]
            const sousGroupes = scission
                ? [
                      {
                          textKey: scission.avecDonnee,
                          workflows: workflows.filter(aDejaRecuDesDonnees),
                      },
                      {
                          textKey: scission.sansDonnee,
                          workflows: workflows.filter(
                              (w) => !aDejaRecuDesDonnees(w)
                          ),
                      },
                  ]
                : [
                      {
                          textKey: varianteDuLibelle(name, workflows[0]),
                          workflows,
                      },
                  ]

            for (const sousGroupe of sousGroupes) {
                // DEV-1625 — LA GARDE : jamais de copie d'incident sans la preuve que
                // NOUS avons été informés. Un workflow qui porte une clé d'incident sans
                // `notifie_le` est ÉCARTÉ (aucun message), jamais rendu avec une date
                // manquante, jamais renvoyé au repli « cette version ne sait pas
                // l'afficher » — qui serait faux : elle sait, c'est la preuve qui manque.
                const servables = sousGroupe.workflows.filter(incidentServable)
                if (servables.length === 0) continue
                const idsources = distinctIdsources(servables)
                // La date citée par la copie est celle du GROUPE, homogène par
                // construction (elle fait partie de la clé de regroupement) : le
                // premier workflow la porte pour tous. Le sous-groupe « sans donnée »
                // n'en a pas, et sa copie n'en cite aucune.
                const derniereMesure = CLES_QUI_CITENT_LA_DERNIERE_MESURE.has(
                    name
                )
                    ? servables[0].verdict?.last_data_date
                    : null
                // DEV-1625 — les variables de l'incident. Homogènes par construction
                // (elles font partie de la clé de regroupement), donc le premier
                // workflow les porte pour tous.
                const incident = LIBELLES_INCIDENT.has(sousGroupe.textKey)
                    ? servables[0].verdict?.incident
                    : null
                const base = t(
                    sousGroupe.textKey,
                    {
                        idsource: idsources.join(", "),
                        ...(derniereMesure
                            ? { derniere_mesure: formatDate(derniereMesure) }
                            : {}),
                        ...(incident
                            ? {
                                  depuis: formatDate(incident.depuis ?? ""),
                                  informe_le: formatDate(
                                      incident.notifie_le ?? ""
                                  ),
                                  // L'ÉLÉMENT RASSURANT, et ses conditions. Servi
                                  // seulement si l'amont l'a MESURÉ (EM, R6X actif,
                                  // sub_cause avec attendu, incident < 30 j) : la
                                  // fenêtre de re-commande glisse depuis aujourd'hui,
                                  // au-delà la phrase serait fausse. Chaîne VIDE
                                  // sinon — jamais une autre promesse à la place.
                                  rattrapage:
                                      incident.rattrapage_automatique === true
                                          ? RATTRAPAGE_AUTOMATIQUE
                                          : "",
                              }
                            : {}),
                        ...variablesDesFlux(name, sousGroupe.workflows[0]),
                        ...variablesDeLAbsence(
                            sousGroupe.textKey,
                            sousGroupe.workflows[0]
                        ),
                        ...variablesDesCommandes(
                            sousGroupe.textKey,
                            sousGroupe.workflows[0]
                        ),
                    },
                    idsources.length
                )
                messages.push({
                    text: avancement ? `${base} ${avancement}` : base,
                    severity,
                })
            }
        }

        if (unknownWorkflows.length > 0) {
            signalerLibellesInconnus(unknownWorkflows)
            const idsources = distinctIdsources(unknownWorkflows)
            messages.push({
                text: t(
                    COPIE_REPLI_LIBELLE_INCONNU,
                    { idsource: idsources.join(", ") },
                    idsources.length
                ),
                severity: "info",
            })
        }

        return messages
    })

    return {
        fetch,
        isFetching,
        hasError,
        getWorkflowEnedisMessageList,
        /**
         * Les workflows BRUTS, avec leur `verdict`. Exposés pour la page de données
         * brutes (DEV-1650) : l'affichage des trous s'y fait DANS la donnée, à sa
         * date, et a besoin des faits datés (`absence_instruite`, `trous_connus`,
         * `streams_en_retard`, `incident`) et non de la copie de bannière.
         *
         * Rien d'autre ne change : la liste est déjà chargée par `fetch`, aucun
         * appel supplémentaire n'est fait.
         */
        workflowEnedisList,
    }
}
